import {
	Box,
	ButtonGroup,
	Flex,
	FormControl,
	FormLabel,
	Select,
	Text,
	Tooltip,
	useColorModeValue,
	useToast,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { ButtonComponent } from 'components/buttons/ButtonComponent';
import Card from 'components/card/Card.js';
import CircularProgress from 'components/chart/CircularProgress';
import { ScrollBar } from 'components/scrollbar/Scrollbar';
import { SidebarHelpContext } from 'contexts/SidebarHelpContext';
import { useContext, useEffect, useState } from 'react';
import { useCreateProjection } from 'services/queryClient/wrapperHooks/projection/useCreateProjection';
import { useGetHolidays } from 'services/queryClient/wrapperHooks/projection/useGetHolidays';
import { useGetProjectionByDate } from 'services/queryClient/wrapperHooks/projection/useGetProjectionByDate';
import { useGetProjectionFilters } from 'services/queryClient/wrapperHooks/projection/useGetProjectionFilters';
import styled from 'styled-components';
import { globalStyles } from 'theme/styles';
import { nameMonths } from 'utils/consts/months';

import { helpCadastrarEstoque } from './variables/helpContent';
import { salesCadenceMessages } from './variables/salesCadenceMessages';

interface IDayData {
	dia: number;
	cadencia_venda_dia: number;
}

const Styles = styled.div`
	width: 100%;

	table {
		border-collapse: collapse;
		text-align: center;
		vertical-align: middle;
		width: 100%;
		border-collapse: collapse;
		border-radius: 6px;
		overflow: hidden;
	}
`;

export default function CadenciaVendas() {
	const { setters } = useContext(SidebarHelpContext);

	const { setDataSidebar } = setters;

	useEffect(() => {
		setDataSidebar(helpCadastrarEstoque);
	}, [setDataSidebar]);
	const toast = useToast();
	const textColor = useColorModeValue('secondaryGray.900', 'white');
	const easyBLUE = useColorModeValue(
		globalStyles.colors.easyBLUE[300],
		globalStyles.colors.easyBLUE[200]
	);
	const easyRED = useColorModeValue(
		globalStyles.colors.easyRED[300],
		'white'
	);
	const greenColor = useColorModeValue(
		globalStyles.colors.green[500],
		globalStyles.colors.green[500]
	);
	const redColor = useColorModeValue(
		globalStyles.colors.easyRED[300],
		globalStyles.colors.easyRED[300]
	);
	const easyBLUE200 = globalStyles.colors.easyBLUE[200];
	const secondaryGray100 = globalStyles.colors.secondaryGray[100];
	const secondaryGray300 = globalStyles.colors.secondaryGray[300];
	const [days, setDays] = useState<IDayData[]>([]);

	const [totalCadencia, setTotalCadencia] = useState(0);

	const queryClient = useQueryClient();

	const anoAtual = new Date().getFullYear();
	const mesAtual = new Date().getMonth() + 1;

	const [mes, setMes] = useState(mesAtual);
	const [ano, setAno] = useState(anoAtual);

	const [hollydays, setHollydays] = useState<Record<number, string>>({});

	const { data: filters } = useGetProjectionFilters(ano);
	const { data: dataHollydays } = useGetHolidays(mes, ano);

	const { data } = useGetProjectionByDate(mes, ano);

	const changeMes = e => {
		setMes(e.target.value);
	};
	const changeAno = e => {
		setAno(e.target.value);
	};
	useEffect(() => {
		if (dataHollydays) {
			const newHollydays = {};
			dataHollydays.forEach(({ date, description }) => {
				const dia = date.substring(8, 10);
				newHollydays[dia] = description;
			});
			setHollydays({ ...newHollydays });
		}
	}, [dataHollydays]);

	const temCasasDecimais = (valor: number): boolean => {
		return valor !== Math.floor(valor);
	};

	useEffect(() => {
		if (data) {
			const days: IDayData[] = [];
			const totalDays = new Date(ano, mes, 0).getDate();
			for (let i = 1; i <= totalDays; i++) {
				const cadence = data.find(item => {
					const dayData = parseInt(item.data.split('-')[2]);
					return dayData === i;
				});
				days.push({
					dia: i,
					cadencia_venda_dia: cadence
						? cadence.cadencia_venda_dia
						: 0,
				});
			}

			setDays(days);

			const totalCadencia = days.reduce((acc, item) => {
				return acc + item.cadencia_venda_dia;
			}, 0);

			let formattedTotal;
			if (temCasasDecimais(totalCadencia)) {
				formattedTotal = totalCadencia.toFixed(2);
			} else {
				formattedTotal = Math.floor(totalCadencia);
			}

			setTotalCadencia(formattedTotal);
		}
	}, [data, ano, mes]);
	const changeCadencia = (e, index) => {
		const newDays = [...days];
		const inputValue = e.target.value;

		const soma =
			totalCadencia +
			Number(inputValue) -
			Number(days[index].cadencia_venda_dia);

		if (soma > 100) {
			toast({
				title: salesCadenceMessages.MSG_ERROR_PROJECTION_EXCEEDED,
				description:
					salesCadenceMessages.MSG_ERROR_PROJECTION_DESCRIPTION,
				position: 'bottom-left',
				status: 'warning',
				duration: 8000,
				isClosable: true,
			});
		} else {
			newDays[index].cadencia_venda_dia = inputValue;
			setDays(newDays);
			somaCadencia();
		}
	};

	const body = {
		ano: ano,
		mes: mes,
		cadencias_venda: days,
	};

	const { mutate, isLoading: isLoadingMutate } = useCreateProjection(body);

	const salvar = () => {
		mutate();
	};

	const cancelar = () => {
		queryClient.invalidateQueries(['projectionByDate']);
	};

	const somaCadencia = () => {
		let soma = 0;
		days.forEach(dia => {
			soma += Number(dia.cadencia_venda_dia);
		});

		let newtotalCadencia;
		if (temCasasDecimais(soma)) {
			newtotalCadencia = +soma.toFixed(2);
		} else {
			newtotalCadencia = Math.floor(soma);
		}
		setTotalCadencia(newtotalCadencia);
	};

	const isWeekendOrHollyday = (
		day,
		month,
		year
	): { color: string; message?: string } => {
		const date = new Date(year, month - 1, day);
		if (hollydays[day]) {
			return {
				message: hollydays[day],
				color: easyRED,
			};
		}
		if (date.getDay() === 0) {
			return {
				message: salesCadenceMessages.MSG_WEEKEND_SUNDAY,
				color: easyRED,
			};
		}
		if (date.getDay() === 6) {
			return {
				message: salesCadenceMessages.MSG_WEEKEND_SATURDAY,
				color: easyBLUE,
			};
		}
		return {
			color: textColor,
		};
	};

	return (
		<Flex
			flexDirection={'column'}
			pt={{ base: '130px', md: '80px', xl: '80px' }}
		>
			<Flex
				justifyContent='space-between'
				mt={'24px'}
				wrap='wrap'
				gap={4}
			>
				<Flex>
					<FormControl
						mr='5px'
						mb='20px'
					>
						<FormLabel mb='0'>
							{salesCadenceMessages.LABELS.YEAR}
						</FormLabel>
						<Select
							data-test='select-ano-page-cadencia_de_vendas'
							placeholder={
								salesCadenceMessages.LABELS.SELECT_PLACEHOLDER
							}
							name={'cod_produto'}
							h='44px'
							borderRadius={10}
							onChange={e => changeAno(e)}
							value={ano}
						>
							{filters?.anos.map((ano, index) => (
								<option
									key={index + 1}
									value={ano.ano}
								>
									{ano.ano}
								</option>
							))}
						</Select>
					</FormControl>

					<FormControl
						mr='5px'
						mb='20px'
					>
						<FormLabel mb='0'>
							{salesCadenceMessages.LABELS.MONTH}
						</FormLabel>
						<Select
							data-test='select-mes-page-cadencia_de_vendas'
							placeholder={
								salesCadenceMessages.LABELS.SELECT_PLACEHOLDER
							}
							name={'mes'}
							h='44px'
							borderRadius={10}
							onChange={e => changeMes(e)}
							value={mes}
						>
							{filters?.meses.map((mes, index) => (
								<option
									key={index + 1}
									value={mes.mes}
								>
									{nameMonths[mes.mes - 1]}
								</option>
							))}
						</Select>
					</FormControl>
				</Flex>
			</Flex>

			<Styles>
				<div
					style={{
						display: 'grid',
						gridTemplateColumns: '80% 20%',
						gap: 10,
					}}
				>
					<Card
						p='13px'
						align='center'
						direction='column'
						w='100%'
					>
						<div>
							<div
								style={{
									display: 'grid',
									gridTemplateColumns: '200px 1fr',
								}}
							>
								<div>
									<div
										style={{
											display: 'grid',
											gridTemplateRows: '1fr 1.5fr',
										}}
									>
										<div
											style={{
												backgroundColor:
													secondaryGray100,
												paddingTop: '10px',
												textAlign: 'center',
												height: '40px',
												borderRight:
													'1px solid' +
													secondaryGray100,
											}}
										>
											{salesCadenceMessages.LABELS.YEAR_DAYS.replace(
												'{year}',
												ano.toString()
											)}
										</div>
										<div
											style={{
												backgroundColor:
													secondaryGray300,
												paddingTop: '20px',
												textAlign: 'center',
												borderRight:
													'1px solid' +
													secondaryGray100,
											}}
										>
											{
												salesCadenceMessages.LABELS
													.PROJECTION_PERCENTAGE
											}
										</div>
									</div>
								</div>
								<ScrollBar color='secondary'>
									<div>
										<div
											style={{
												display: 'grid',
												gridTemplateRows: '1fr 1.5fr',
											}}
										>
											<div
												style={{
													backgroundColor:
														secondaryGray100,
													textAlign: 'center',
													height: '40px',
													display: 'grid',
													gridTemplateColumns: `repeat(${days.length}, 55px)`,
												}}
											>
												{days.map((dia, index) => {
													const isWeekend =
														isWeekendOrHollyday(
															dia.dia,
															mes,
															ano
														);
													if (isWeekend.message) {
														return (
															<Tooltip
																label={
																	isWeekend.message
																}
																placement='top'
																bg='gray.200'
																color={
																	'blue.800'
																}
																hasArrow
																arrowSize={15}
															>
																<Text
																	key={index}
																	color={
																		isWeekend.color
																	}
																	style={{
																		paddingTop:
																			'10px',
																		borderRight:
																			'1px solid' +
																			secondaryGray100,
																	}}
																>
																	{dia.dia < 9
																		? '0' +
																		  dia.dia
																		: dia.dia}
																</Text>
															</Tooltip>
														);
													}
													return (
														<div
															key={index}
															style={{
																paddingTop:
																	'10px',
																borderRight:
																	'1px solid' +
																	secondaryGray100,
																color: isWeekend.color,
															}}
														>
															{dia.dia < 9
																? '0' + dia.dia
																: dia.dia}
														</div>
													);
												})}
											</div>
											<div
												style={{
													backgroundColor:
														secondaryGray300,
													display: 'grid',
													textAlign: 'center',
													gridTemplateColumns: `repeat(${days.length}, 55px)`,
												}}
											>
												{!isLoadingMutate &&
													days.map((dia, index) => (
														<div
															key={index}
															style={{
																backgroundColor:
																	secondaryGray300,
																textAlign:
																	'center',
																borderRight:
																	'1px solid' +
																	secondaryGray100,
																paddingTop:
																	'10px',
																paddingLeft:
																	'10px',
																paddingRight:
																	'10px',
															}}
														>
															<input
																type='number'
																placeholder={
																	salesCadenceMessages
																		.PLACEHOLDER
																		.PERCENTAGE
																}
																style={{
																	width: '100%',
																	textAlign:
																		'center',
																	height: '40px',
																	marginRight:
																		'5px',
																	border:
																		'1px solid' +
																		secondaryGray100,
																	borderRadius:
																		'10px',
																}}
																value={
																	dia.cadencia_venda_dia
																}
																onChange={e =>
																	changeCadencia(
																		e,
																		index
																	)
																}
															/>
														</div>
													))}
											</div>
										</div>
									</div>
								</ScrollBar>
							</div>

							<Flex
								style={{ backgroundColor: easyBLUE200 }}
								h='50px'
								marginTop='20px'
							>
								<Flex
									marginLeft='auto'
									paddingRight={'20px'}
									paddingTop='5px'
								>
									<ButtonGroup spacing={'10px'}>
										<ButtonComponent
											data-test='button-cancelar-page-cadencia_de_vendas'
											type={'ghost'}
											title={
												salesCadenceMessages.LABELS
													.CANCEL
											}
											onClick={() => cancelar()}
											textColor={'easy360.bgWhite'}
											_hover={{
												color: easyBLUE,
												backgroundColor:
													'easy360.bgWhite',
											}}
										/>
										<ButtonComponent
											data-test='button-salvar-page-cadencia_de_vendas'
											type={'primary'}
											title={
												salesCadenceMessages.LABELS.SAVE
											}
											onClick={() => salvar()}
										/>
									</ButtonGroup>
								</Flex>
							</Flex>
						</div>
					</Card>
					<div>
						<Card
							p='13px'
							align='center'
							direction='column'
							w='100%'
						>
							<Text
								color={textColor}
								fontSize='2xl'
								fontWeight='700'
								mb='0px'
								mx='auto'
								textAlign={'center'}
							>
								{
									salesCadenceMessages.LABELS
										.MONTHLY_PROJECTION_TOTAL
								}
							</Text>
							<Text
								color='secondaryGray.600'
								fontSize='sm'
								fontWeight='500'
								maxW='350px'
								mx='auto'
								mb='10px'
							>
								{salesCadenceMessages.LABELS.CHECK_MONTH}
							</Text>

							<Box
								w='120px'
								mx='auto'
								mb='10px'
								mt='0px'
							>
								<CircularProgress
									percentage={totalCadencia}
									color={
										totalCadencia > 60
											? greenColor
											: redColor
									}
								/>
							</Box>
						</Card>
					</div>
				</div>
			</Styles>
		</Flex>
	);
}
